import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../components/atoms/Button';
import styles from '../login/Login.module.css';
import { LoginService } from '../../services/LoginService';
import { UserService } from '../../services/user/UserService';

const AccountActivationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [placeId, setPlaceId] = React.useState<number | null>(null);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, boolean>>({});
  const [passwordHelperText, setPasswordHelperText] = React.useState('');
  const [confirmHelperText, setConfirmHelperText] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  const minPasswordLength = 8;
  const passwordHasMinLength = password.length >= minPasswordLength;
  const passwordHasLetter = /[A-Za-z]/.test(password);
  const passwordHasNumber = /\d/.test(password);
  const isPasswordComplex = passwordHasMinLength && passwordHasLetter && passwordHasNumber;
  const isConfirmValid = password === confirmPassword;

  const passwordStatusText =
    password.trim().length > 0
      ? isPasswordComplex
        ? 'Senha atende aos requisitos'
        : 'Use no mínimo 8 caracteres com letras e números'
      : '';

  const confirmStatusText =
    confirmPassword.trim().length > 0
      ? isConfirmValid
        ? 'Senhas conferem'
        : 'As senhas não conferem'
      : '';

  React.useEffect(() => {
    const activationCode = searchParams.get('code') || '';
    if (!activationCode) {
      setErrorMessage('Link de ativação inválido.');
      setLoading(false);
      return;
    }

    setCode(activationCode);
    const fetchActivationData = async () => {
      try {
        const data = await UserService.activationGet(activationCode);
        setName(data.name || '');
        setEmail(data.email || '');
        setPlaceId(Number(data.placeId));
      } catch (err: any) {
        setErrorMessage(err?.data?.detail || 'Não foi possível carregar os dados de ativação.');
      } finally {
        setLoading(false);
      }
    };

    fetchActivationData();
  }, [searchParams]);

  const validate = () => {
    const newErrors: Record<string, boolean> = {};
    setPasswordHelperText('');
    setConfirmHelperText('');

    if (!name.trim()) newErrors.name = true;
    if (!password.trim()) newErrors.password = true;
    if (!confirmPassword.trim()) newErrors.confirmPassword = true;

    if (password.trim() && !isPasswordComplex) {
      newErrors.password = true;
      setPasswordHelperText('Mínimo 8 caracteres, com letras e números');
    }

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = true;
      setConfirmHelperText('As senhas não conferem');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setMessage('');
    setErrorMessage('');

    if (!validate()) return;
    if (!code) {
      setErrorMessage('Código de ativação ausente.');
      return;
    }

    setSubmitting(true);
    try {
      const loginData = await UserService.activationUpdate({
        name,
        email,
        password,
        code,
        placeId: placeId || 0,
      });

      localStorage.setItem('token', loginData.token);
      localStorage.setItem('token_date', new Date().getTime().toString());
      LoginService.setUserType(loginData.userType);
      setMessage('Conta ativada com sucesso. Redirecionando...');
      window.setTimeout(() => {
        navigate('/listar/espaco');
      }, 900);
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || 'Não foi possível concluir a ativação.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={styles.page}>
      <Box className={styles.leftPanel}>
        <div className={styles.leftContent} />
      </Box>

      <Box className={styles.rightPanel}>
        <Box className={styles.formCard}>
          <img
            className={styles.brandLogo}
            src="https://temporario.umespaco.com/wp-content/uploads/2024/09/umespaco-com.png"
            alt="UmEspaco"
          />
          <div className={styles.formTitle}>Ativação de Conta</div>
          <div className={styles.divider} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={styles.formStack}>
                <FormControl fullWidth>
                  <TextField
                    required
                    variant="outlined"
                    label="Nome completo"
                    value={name}
                    error={!!errors.name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    variant="outlined"
                    label="Endereço de e-mail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    error={!!errors.password}
                    helperText={passwordHelperText || passwordStatusText}
                    variant="outlined"
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={(ev) => ev.preventDefault()}
                            onMouseUp={(ev) => ev.preventDefault()}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    label="Confirmar senha"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    error={!!errors.confirmPassword}
                    helperText={confirmHelperText || confirmStatusText}
                    variant="outlined"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={(ev) => ev.preventDefault()}
                            onMouseUp={(ev) => ev.preventDefault()}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                {!!message && <Alert severity="success">{message}</Alert>}

                <FormControl fullWidth>
                  <Button
                    type="submit"
                    variant="contained"
                    label={submitting ? 'Salvando...' : 'Ativar conta'}
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{
                      backgroundColor: '#1f75cf',
                      color: '#ffffff',
                      fontWeight: 600,
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(31, 117, 207, 0.2)',
                      '&:hover': {
                        backgroundColor: '#1a67b6',
                      },
                    }}
                  />
                </FormControl>
              </div>
            </form>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountActivationPage;
